import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { toast } from "react-toastify";

// API URL
const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000/api";

// Initial state
const initialState = {
  members: [],
  member: null,
  loading: false,
  exporting: false,
  error: null,
  currentMember: null,
  // New pagination object
  pagination: {
    page: 1,
    per_page: 10,
    total: 0,
    total_pages: 0
  },
  // Keep backward compatibility
  totalMembers: 0,
  totalPages: 0,
  currentPage: 1,
  filters: {
    search: "",
    tier: "",
    status: "",
  },
};

// Get all members
export const getMembers = createAsyncThunk(
  "members/getMembers",
  async (
    { 
      page = 1, 
      perPage = 10, 
      search = "", 
      tier = "", 
      status = "",
      sortBy = "",
      minTickets = 0,
      maxTickets = 1000,
      minGiveaways = 0,
      maxGiveaways = 100
    },
    { rejectWithValue }
  ) => {
    try {
      let url = `${API_URL}/members?page=${page}&per_page=${perPage}`;

      if (search) {
        url += `&search=${search}`;
      }

      if (tier) {
        url += `&tier=${tier}`;
      }

      if (status) {
        url += `&status=${status}`;
      }

      if (sortBy) {
        url += `&sortBy=${sortBy}`;
      }

      if (minTickets !== undefined && minTickets !== 0) {
        url += `&minTickets=${minTickets}`;
      }

      if (maxTickets !== undefined && maxTickets !== 1000) {
        url += `&maxTickets=${maxTickets}`;
      }

      if (minGiveaways !== undefined && minGiveaways !== 0) {
        url += `&minGiveaways=${minGiveaways}`;
      }

      if (maxGiveaways !== undefined && maxGiveaways !== 100) {
        url += `&maxGiveaways=${maxGiveaways}`;
      }

      const response = await axios.get(url);
      
      // Handle both old and new pagination response formats
      const responseData = response.data;
      const pagination = responseData.pagination || {
        page: page,
        per_page: perPage,
        total: parseInt(responseData?.totalUsers || 0),
        total_pages: parseInt(responseData?.totalPages || 0)
      };
      
      return {
        members: responseData?.members || [],
        pagination: {
          page: pagination.page,
          per_page: pagination.per_page,
          total: pagination.total,
          total_pages: pagination.total_pages
        },
        // Keep backward compatibility
        totalMembers: pagination.total,
        totalPages: pagination.total_pages,
        currentPage: pagination.page,
        filters: { search, tier, status, sortBy, minTickets, maxTickets, minGiveaways, maxGiveaways },
      };
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to fetch members";
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

// Get member by ID
export const getMemberById = createAsyncThunk(
  "members/getMemberById",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${API_URL}/members/${id}`);
      console.log("Fetched member:", response.data);
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to fetch member";
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

// Update member
export const updateMember = createAsyncThunk(
  "members/updateMember",
  async ({ id, memberData }, { rejectWithValue }) => {
    try {
      const response = await axios.put(`${API_URL}/members/${id}`, memberData);
      toast.success("Member updated successfully");
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to update member";
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

// Get member stats
export const getMemberStats = createAsyncThunk(
  "members/getMemberStats",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${API_URL}/stats/member/${id}`);
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to fetch member stats";
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

// Export members to CSV
export const exportMembers = createAsyncThunk(
  "members/exportMembers",
  async (filters = {}, { rejectWithValue }) => {
    try {
      let url = `${API_URL}/members`;

      // Add filters to query params if provided
      const queryParams = new URLSearchParams();
      // Add export=true to get all members without pagination
      queryParams.append("export", "true");
      queryParams.append("per_page", "all");
      
      if (filters.search) queryParams.append("search", filters.search);
      if (filters.tier) queryParams.append("tier", filters.tier);
      if (filters.status) queryParams.append("status", filters.status);

      url += `?${queryParams.toString()}`;

      const response = await axios.get(url);

      // Create a download link and trigger download
      const csvContent = response.data?.members
        .map((member) => {
          const name = `${member.customer_data?.first_name || ""} ${
            member.customer_data?.last_name || ""
          }`.trim();
          const id = member.id || '';
          const username = member.customer_data?.username || '';
          const email = member.customer_data?.email || '';
          const status = member.status || '';
          const totalEntries = member.total_entries || 0;
          const giveawaysWon = member.giveawaysWon || 0;
          
          // Escape commas and quotes in CSV data
          const escapeCSV = (field) => {
            if (typeof field === 'string' && (field.includes(',') || field.includes('"') || field.includes('\n'))) {
              return `"${field.replace(/"/g, '""')}"`;
            }
            return field;
          };
          
          return `${escapeCSV(id)},${escapeCSV(name)},${escapeCSV(username)},${escapeCSV(email)},${escapeCSV(status)},${totalEntries},${giveawaysWon}`;
        })
        .join("\n");

      const csvBlob = new Blob([`ID,Name,Username,Email,Status,Tickets,Giveaways Won\n${csvContent}`], {
        type: "text/csv",
      });
      const downloadUrl = window.URL.createObjectURL(csvBlob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.setAttribute(
        "download",
        `members-export-${new Date().toISOString().split("T")[0]}.csv`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success("Members exported successfully");
      return true;
    } catch (error) {
      console.error("Error exporting members:", error);
      const message =
        error.response?.data?.message || "Failed to export members";
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

// Members slice
const membersSlice = createSlice({
  name: "members",
  initialState,
  reducers: {
    resetMembersError: (state) => {
      state.error = null;
    },
    clearMember: (state) => {
      state.member = null;
    },
    setFilters: (state, action) => {
      state.filters = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Get members cases
      .addCase(getMembers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getMembers.fulfilled, (state, action) => {
        state.loading = false;
        state.members = action.payload?.members || [];
        
        // Update new pagination object
        if (action.payload?.pagination) {
          state.pagination = action.payload.pagination;
        }
        
        // Keep backward compatibility
        state.totalMembers = action.payload?.totalMembers || 0;
        state.totalPages = action.payload?.totalPages || 0;
        state.currentPage = action.payload?.currentPage || 1;
        state.filters = action.payload?.filters || { search: "", tier: "" };
        state.error = null;
      })
      .addCase(getMembers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Get member by ID cases
      .addCase(getMemberById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getMemberById.fulfilled, (state, action) => {
        state.loading = false;
        state.member = action.payload;
        state.currentMember = action.payload;
        state.error = null;
      })
      .addCase(getMemberById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Update member cases
      .addCase(updateMember.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateMember.fulfilled, (state, action) => {
        state.loading = false;
        state.member = action.payload;

        // Update member in members list if it exists
        const index = state.members.findIndex(
          (m) => m.id === action.payload.id
        );
        if (index !== -1) {
          state.members[index] = action.payload;
        }

        state.error = null;
      })
      .addCase(updateMember.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Get member stats cases
      .addCase(getMemberStats.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getMemberStats.fulfilled, (state, action) => {
        state.loading = false;
        state.member = { ...state.member, stats: action.payload };
        state.error = null;
      })
      .addCase(getMemberStats.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Export members cases
      .addCase(exportMembers.pending, (state) => {
        state.exporting = true;
        state.error = null;
      })
      .addCase(exportMembers.fulfilled, (state) => {
        state.exporting = false;
        state.error = null;
      })
      .addCase(exportMembers.rejected, (state, action) => {
        state.exporting = false;
        state.error = action.payload;
      });
  },
});

export const { resetMembersError, clearMember, setFilters } =
  membersSlice.actions;

export default membersSlice.reducer;
